import React, { useCallback, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from './AlertDialog';

/**
 * useConfirm — an imperative confirm() built on the 2.0 AlertDialog, for the
 * common "ask before a destructive action" case (the ergonomics of the old
 * window.confirm / useConfirmDialog, but the 2.0 dialog + a11y).
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   // render once, anywhere in the component's JSX:
 *   {confirmDialog}
 *   // call it imperatively; it resolves a boolean:
 *   const ok = await confirm({
 *     title: 'Delete role?',
 *     description: 'This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *     media: <Icon name="delete" />,
 *   });
 *   if (ok) await deleteRole(id);
 *
 * Single-slot: one pending confirm at a time (a new call supersedes a pending
 * one, resolving it false). Resolves true on the Action, false on Cancel /
 * Escape / programmatic close.
 */

const DEFAULTS = {
  title: 'Are you sure?',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  destructive: false,
  size: 'sm',
  media: null,
};

export function useConfirm() {
  const [state, setState] = useState({ open: false, opts: DEFAULTS });
  const resolver = useRef(null);

  const settle = useCallback((result) => {
    const r = resolver.current;
    resolver.current = null;
    setState((s) => ({ ...s, open: false }));
    if (r) r(result);
  }, []);

  const confirm = useCallback((opts = {}) => {
    // Supersede any still-pending confirm.
    if (resolver.current) {
      resolver.current(false);
      resolver.current = null;
    }
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ open: true, opts: { ...DEFAULTS, ...opts } });
    });
  }, []);

  const { open, opts } = state;

  const confirmDialog = (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) settle(false); }}>
      <AlertDialogContent size={opts.size}>
        <AlertDialogHeader>
          {opts.media && <AlertDialogMedia>{opts.media}</AlertDialogMedia>}
          <AlertDialogTitle>{opts.title}</AlertDialogTitle>
          {opts.description && (
            <AlertDialogDescription>{opts.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => settle(false)}>
            {opts.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={opts.destructive ? 'destructive' : 'default'}
            onClick={() => settle(true)}
          >
            {opts.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, confirmDialog };
}

export default useConfirm;
